import { titleize } from "helpers/string";
import { EmailTemplate } from "../EmailTemplate/models";
import { MessageTemplate } from "../MessageTemplate/models";
import { taskTemplatePrimaryTrigger } from "../TaskTemplate/helpers";
import { Template, TemplatePrefixId, TemplateSummary } from "./models";
import { ActionType, TemplateType } from "../Shared/models";

export function messageEmailTemplatePrimaryTrigger(template: MessageTemplate | EmailTemplate) {
  return template?.deliverAtTrigger;
}

export function primaryTemplateTrigger(template: TemplateSummary) {
  switch (template?.templateType) {
    case "message_template":
    case "email_template":
      return messageEmailTemplatePrimaryTrigger(template?.template);
    case "task_template":
      return taskTemplatePrimaryTrigger(template?.template);
    case "checkpoint_template":
      return template?.template?.evaluateAtTrigger;
    case "meeting_template":
    default:
      return undefined;
  }
}

export function templateTimingSortValue(template: TemplateSummary) {
  switch (template?.templateType) {
    case "meeting_template":
      return template.template.sortValue;
    case "message_template":
    case "email_template":
      return messageEmailTemplatePrimaryTrigger(template?.template)?.sortValue;
    case "task_template":
      return taskTemplatePrimaryTrigger(template?.template)?.sortValue;
    case "checkpoint_template":
      return template?.template?.evaluateAtTrigger?.sortValue;
    default:
      return undefined;
  }
}

export function templateTimingDescriptor(template: TemplateSummary) {
  switch (template?.templateType) {
    case "meeting_template":
      return template.template.proposedAtDescriptor;
    case "message_template":
    case "email_template":
    case "task_template":
    case "checkpoint_template":
      return primaryTemplateTrigger(template)?.data?.descriptor;
    default:
      return undefined;
  }
}

export function sortTemplatesByTiming(templates: TemplateSummary[] | undefined): TemplateSummary[] {
  if (!templates) return [];
  return [...templates].sort((a, b) => {
    const aVal = templateTimingSortValue(a) ?? Infinity;
    const bVal = templateTimingSortValue(b) ?? Infinity;
    return aVal - bVal;
  });
}

export function filterTemplateSummaryByText(templates: TemplateSummary[] | undefined, searchText: string | "") {
  if (!templates) return [];
  if ((searchText || "").length < 3) {
    return templates;
  }

  const text = searchText.toLowerCase();
  return templates.filter((template) => template?.template?.name?.toLowerCase()?.includes(text));
}

export function fromTemplatePrefixId(templatePrefixId: TemplatePrefixId | undefined) {
  if (!templatePrefixId) {
    return {};
  }
  const match = templatePrefixId.match(/(.+?)_(\d+)$/);
  if (match) {
    const [, templateType, id] = match;
    const resourceType = templateType.split("_")?.[0];
    return {
      templateType: templateType as unknown as TemplateType,
      resourceType: resourceType as unknown as ActionType,
      id,
    };
  }
  return {};
}

export function confirmDeleteTemplateArgs(templateSummary: TemplateSummary | Template) {
  const resolvedTemplate = (templateSummary as TemplateSummary)?.templateType
    ? (templateSummary as TemplateSummary).template
    : templateSummary;
  const { templateType, resourceType } = fromTemplatePrefixId(resolvedTemplate?.prefixId);
  return {
    header: `Confirm Delete ${titleize(templateType as string)}`,
    body: `This cannot be undone! Be sure you want to delete this template. All scheduled ${resourceType}s from this template will remain scheduled.`,
    confirmButtonText: "Confirm Delete",
  };
}

export function confirmRemoveFromPath(templateSummary: TemplateSummary | Template) {
  const resolvedTemplate = (templateSummary as TemplateSummary)?.templateType
    ? (templateSummary as TemplateSummary).template
    : templateSummary;
  const { templateType } = fromTemplatePrefixId(resolvedTemplate?.prefixId);
  return {
    header: `Confirm Remove ${titleize(templateType as string)}`,
    body: "This will remove this template from the path",
    confirmButtonText: "Confirm Remove",
  };
}

export function isTemplateSummary(obj: any): obj is TemplateSummary {
  return obj && typeof obj.templateType === "string" && "template" in obj;
}

export function extractTemplate(templateOrSummary: Template | TemplateSummary): Template {
  return isTemplateSummary(templateOrSummary) ? templateOrSummary.template : templateOrSummary;
}
